import type { TrendsResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { BackIcon, CalendarIcon } from '@/components/pulse/icons';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { useTrends } from '@/domains/trends/api';

export type TrendTab = 'built' | 'merges';

/** A chart's top: a round number at or above the largest value, so the four gridlines read cleanly. */
export function niceTop(max: number): number {
  if (max <= 4) return 4;
  const rough = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 10].find((candidate) => candidate * magnitude >= rough) ?? 10;
  return step * magnitude * 4;
}

const columns = (count: number) => ({ gridTemplateColumns: `repeat(${count},minmax(0,1fr))`, gap: count > 4 ? 10 : 24 });

/** The prototype's stacked week chart: a y axis, a column per week stacked by segment, and the totals under it. */
function StackedChart({
  label,
  weeks,
  stacks,
}: {
  readonly label: string;
  readonly weeks: ReadonlyArray<{ readonly monday: string; readonly label: string }>;
  readonly stacks: ReadonlyArray<ReadonlyArray<{ readonly name: string; readonly colour: string; readonly value: number }>>;
}): React.ReactElement {
  const totals = stacks.map((stack) => stack.reduce((sum, segment) => sum + segment.value, 0));
  const top = niceTop(Math.max(0, ...totals));
  return (
    <>
      <div className="chart" role="img" aria-label={`${label}: ${weeks.map((week, index) => `${week.label} ${totals[index]}`).join(', ')}`}>
        <div className="yaxis num" style={{ height: 240 }}>
          {[top, (top * 3) / 4, top / 2, top / 4, 0].map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
        </div>
        <div className="tplot" style={columns(weeks.length)}>
          {stacks.map((stack, index) => (
            <div key={weeks[index]?.monday} className="tcol">
              <div className="tstack" style={{ height: `${(((totals[index] ?? 0) / top) * 100).toFixed(1)}%` }} title={`${totals[index]}`}>
                {stack
                  .filter((segment) => segment.value > 0)
                  .map((segment) => (
                    <i key={segment.name} style={{ flex: segment.value, background: segment.colour }} title={`${segment.name}: ${segment.value}`} />
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="txaxis" style={columns(weeks.length)}>
        {weeks.map((week, index) => (
          <span key={week.monday}>
            <b className="num">{totals[index]}</b> · {week.label}
          </span>
        ))}
      </div>
    </>
  );
}

function Legend({ items }: { readonly items: ReadonlyArray<{ readonly name: string; readonly colour: string }> }): React.ReactElement {
  return (
    <div className="legend">
      {items.map((item) => (
        <span key={item.name}>
          <i className="sw" style={{ background: item.colour }} />
          {item.name}
        </span>
      ))}
    </div>
  );
}

function MiniBars({ weeks, values, labels, colour }: { readonly weeks: ReadonlyArray<{ readonly monday: string; readonly label: string }>; readonly values: ReadonlyArray<number>; readonly labels?: ReadonlyArray<string>; readonly colour?: string }): React.ReactElement {
  const top = Math.max(1, ...values);
  return (
    <div className="mini-bars">
      {weeks.map((week, index) => (
        <div key={week.monday} className="stack" style={{ gap: 4, alignItems: 'center', flex: 1 }}>
          <span className="num" style={{ fontSize: 12, color: 'var(--muted)' }}>
            {labels?.[index] ?? values[index]}
          </span>
          <i style={{ height: `${Math.max(((values[index] ?? 0) / top) * 90, 2).toFixed(0)}px`, ...(colour === undefined ? {} : { background: colour }) }} />
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{week.label}</span>
        </div>
      ))}
    </div>
  );
}

/** The view and period from the URL: `?view=month`, `?period=` any date in it; weeks and the current period by default. */
type BuiltCardData = Pick<TrendsResponse['built']['members'][number], 'perWeek' | 'thisWeek' | 'unlinkedPercent' | 'unlinkedFlagged'>;

/** A developer's or a project's Built (TRN-001 AC-5, TRN-003 AC-2): per column, finished and still open, on a shared scale. */
function BuiltCard({
  title,
  name,
  card,
  top,
  weeks,
  when,
  column,
}: {
  readonly title: React.ReactNode;
  readonly name: string;
  readonly card: BuiltCardData;
  readonly top: number;
  readonly weeks: ReadonlyArray<{ readonly monday: string; readonly label: string }>;
  readonly when: string;
  readonly column: string;
}): React.ReactElement {
  return (
    <article className="card sm">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        {title}
        <span className="num">
          <b style={{ fontSize: 22 }}>{card.thisWeek.items}</b> <span style={{ fontSize: 12, color: 'var(--muted)' }}>built {when}</span>
        </span>
      </div>
      <div className="sm-plot" style={{ gridTemplateColumns: `repeat(${weeks.length},minmax(0,1fr))` }} role="img" aria-label={`${name}, built per ${column}: ${card.perWeek.map((week) => week.items).join(', ')}`}>
        {card.perWeek.map((week, index) => (
          <div key={weeks[index]?.monday} className="tcol">
            <div className="tstack" style={{ height: `${((week.items / top) * 100).toFixed(1)}%` }}>
              {week.finished > 0 ? <i style={{ flex: week.finished, background: 'var(--s-acc)' }} title={`Finished: ${week.finished}`} /> : null}
              {week.open > 0 ? <i style={{ flex: week.open, background: 'var(--s-ready)' }} title={`Still open: ${week.open}`} /> : null}
            </div>
          </div>
        ))}
      </div>
      <div className="sm-x" style={{ gridTemplateColumns: `repeat(${weeks.length},minmax(0,1fr))` }}>
        {weeks.map((week, index) => (
          <span key={week.monday}>
            <b className="num" style={{ color: 'var(--ink)' }}>
              {card.perWeek[index]?.items ?? 0}
            </b>
            <br />
            {week.label}
          </span>
        ))}
      </div>
      <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
        {when.charAt(0).toUpperCase() + when.slice(1)}: {card.thisWeek.finished} finished, {card.thisWeek.open} still open. {card.thisWeek.stories} stories
        {card.thisWeek.tasks > 0 ? `, ${card.thisWeek.tasks} Teamwork tasks` : ''}
        {card.thisWeek.unlinked > 0 ? `, ${card.thisWeek.unlinked} with no story or task` : ''}.
      </span>
      {card.unlinkedFlagged ? (
        <span className="chip c-warn" style={{ alignSelf: 'flex-start' }}>
          {card.unlinkedPercent}% unlinked
        </span>
      ) : null}
    </article>
  );
}

export function trendsSearch(view: 'week' | 'month', period: string | null): string {
  const parts = [view === 'month' ? 'view=month' : '', period === null ? '' : `period=${period}`].filter(Boolean);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

/** TRN-1: weeks or months, and previous and next a period at a time; next stops at the current one. */
export function TrendsControl({ data, onChange }: { readonly data: TrendsResponse; readonly onChange: (view: 'week' | 'month', period: string | null) => void }): React.ReactElement {
  return (
    <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
      <div className="seg" role="group" aria-label="View">
        <button type="button" aria-pressed={data.view === 'week'} onClick={() => onChange('week', null)}>
          Weeks
        </button>
        <button type="button" aria-pressed={data.view === 'month'} onClick={() => onChange('month', null)}>
          Months
        </button>
      </div>
      <div className="row" style={{ gap: 6 }} role="group" aria-label={data.view === 'week' ? 'Weeks shown' : 'Months shown'}>
        <button type="button" className="btn" aria-label={data.view === 'week' ? 'Previous week' : 'Previous month'} onClick={() => onChange(data.view, data.previous)}>
          <BackIcon />
        </button>
        <span className="datepill">
          <CalendarIcon />
          <span>{data.rangeLabel.charAt(0).toUpperCase() + data.rangeLabel.slice(1)}</span>
        </span>
        <button
          type="button"
          className="btn"
          aria-label={data.view === 'week' ? 'Next week' : 'Next month'}
          disabled={data.next === null}
          title={data.next === null ? `This is the current ${data.view}` : undefined}
          onClick={() => onChange(data.view, data.next)}
        >
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
            <BackIcon />
          </span>
        </button>
      </div>
    </div>
  );
}

/** Trends (TRN-1 to TRN-5), built from the prototype's `viewTrends()`. */
export function TrendsPage(): React.ReactElement {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const view = search.get('view') === 'month' ? 'month' : 'week';
  const period = search.get('period');
  const page = useTrends(view, period);
  const data = page.data;
  const go = (nextView: 'week' | 'month', nextPeriod: string | null) => navigate({ search: trendsSearch(nextView, nextPeriod) });
  return (
    <>
      <PageHeader
        eyebrow={`Trends · ${data?.rangeLabel ?? ''}`}
        title={view === 'week' ? 'How the days compare' : 'How the weeks compare'}
        sub={data?.currentNote ?? ''}
        right={data !== undefined ? <TrendsControl data={data} onChange={go} /> : undefined}
      />
      {data !== undefined ? <TrendsView data={data} /> : null}
    </>
  );
}

export function TrendsView({ data, initialTab = 'built' }: { readonly data: TrendsResponse; readonly initialTab?: TrendTab }): React.ReactElement {
  const [tab, setTab] = React.useState<TrendTab>(initialTab);
  const { built, merges, side } = data;
  const weeks = data.periods.map((period) => ({ monday: period.key, label: period.label }));
  const when = `${data.isCurrent ? 'this' : 'that'} ${data.view}`;
  // A week shows a column per day; a month, a column per week.
  const column = data.view === 'week' ? 'day' : 'week';
  const maxCycle = Math.max(1, ...side.cycle.map((entry) => entry.p75Hours));
  const smallTop = Math.max(1, ...built.members.flatMap((member) => member.perWeek.map((week) => week.items)));
  // The project cards share a scale of their own: a project's items aren't one person's.
  const projectTop = Math.max(1, ...side.projects.flatMap((project) => project.perWeek.map((week) => week.items)));
  return (
    <>
      <div className="tabsbar">
        <div className="seg" role="group" aria-label="Measure" style={{ marginBottom: 10 }}>
          <button type="button" aria-pressed={tab === 'built'} onClick={() => setTab('built')}>
            Built
          </button>
          <button type="button" aria-pressed={tab === 'merges'} onClick={() => setTab('merges')}>
            All merges and reviews
          </button>
        </div>
      </div>
      <Kpis items={(tab === 'built' ? built.tiles : merges.tiles).map((tile) => ({ label: tile.label, value: tile.value, sub: tile.sub, warn: tile.warn }))} />
      {data.missing.length > 0 ? <span style={{ fontSize: 13, color: 'var(--muted)' }}>No figures were stored for {data.view === 'week' ? 'the weeks of ' : ''}{data.missing.join(', ')}.</span> : null}
      <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 380px' }}>
        <div className="stack" style={{ gap: 20 }}>
          {tab === 'built' ? (
            <>
              <section className="card pad stack" style={{ gap: 14 }}>
                <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <h2>Built per {column}</h2>
                  <Legend
                    items={[
                      { name: 'Finished by the end of that week', colour: 'var(--s-acc)' },
                      { name: 'Still open', colour: 'var(--s-ready)' },
                    ]}
                  />
                </div>
                <StackedChart
                  label={`Built per ${column}`}
                  weeks={weeks}
                  stacks={built.perWeek.map((week) => [
                    { name: 'Finished', colour: 'var(--s-acc)', value: week.finished },
                    { name: 'Still open', colour: 'var(--s-ready)', value: week.open },
                  ])}
                />
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  Built counts items: a story, a Teamwork task, or a PR with neither. An item counts in every {data.view} someone wrote a commit for it, by the date the commit was written, and once per person per {data.view}, however many times they committed. Release PRs carry develop&apos;s work a second time and are left out.
                </span>
              </section>
              <section className="stack" style={{ gap: 14 }} aria-labelledby="bdh">
                <h2 id="bdh">Built by developer</h2>
                <div className="sm-grid">
                  {built.members.map((member) => (
                    <BuiltCard
                      key={member.userId}
                      title={
                        <Link className="row" to={member.href} style={{ gap: 8, fontWeight: 600, textDecoration: 'none', color: 'var(--ink)' }}>
                          <i className="sw" style={{ background: member.colour, width: 10, height: 10 }} />
                          {member.name}
                        </Link>
                      }
                      name={member.name}
                      card={member}
                      top={smallTop}
                      weeks={weeks}
                      when={when}
                      column={column}
                    />
                  ))}
                </div>
              </section>
            </>
          ) : (
            <>
              <section className="card pad stack" style={{ gap: 14 }}>
                <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <h2>All PRs merged per {column}, by person</h2>
                  <Legend items={merges.merged} />
                </div>
                <StackedChart label={`PRs merged per ${column}`} weeks={weeks} stacks={weeks.map((_, index) => merges.merged.map((entry) => ({ name: entry.name, colour: entry.colour, value: entry.values[index] ?? 0 })))} />
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>Every merged PR, housekeeping included. PRs by people off the roster, such as bots, are counted apart.</span>
              </section>
              <section className="card pad stack" style={{ gap: 14 }}>
                <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <h2>Reviews per {column}, by reviewer</h2>
                  <Legend items={merges.reviews} />
                </div>
                <StackedChart label={`PRs reviewed per ${column}`} weeks={weeks} stacks={weeks.map((_, index) => merges.reviews.map((entry) => ({ name: entry.name, colour: entry.colour, value: entry.values[index] ?? 0 })))} />
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>Distinct PRs each person reviewed that {column}, their own PRs left out.</span>
              </section>
            </>
          )}
        </div>
        <aside className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 10 }}>
            <h2>To production per {column}</h2>
            <MiniBars weeks={weeks} values={side.toProduction} />
          </section>
          {/* TRN-003 AC-2: Built per project, the developer cards' shape, busiest first. */}
          {side.projects.map((project) => (
            <BuiltCard
              key={project.productName}
              title={
                <Link to={project.href} style={{ fontWeight: 600, textDecoration: 'none', color: 'var(--ink)' }}>
                  {project.productName}
                </Link>
              }
              name={project.productName}
              card={project}
              top={projectTop}
              weeks={weeks}
              when={when}
              column={column}
            />
          ))}
          <section className="card pad stack" style={{ gap: 10 }}>
            <h2>Time from PR opened to merged</h2>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Median, with the slowest quarter&apos;s start. Release PRs and time as a draft are left out.</span>
            {side.cycle.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>No PR merged in these weeks.</span> : null}
            {side.cycle.map((entry) => (
              <div key={entry.name} className="stack" style={{ gap: 6 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span>{entry.name}</span>
                  <span className="num">
                    <b>{entry.medianHours} h</b> <span style={{ color: 'var(--muted)' }}>· {entry.p75Hours} h</span>
                  </span>
                </div>
                <div className="meter" style={{ position: 'relative' }} role="img" aria-label={`${entry.name}: median ${entry.medianHours} hours, 75th percentile ${entry.p75Hours} hours, ${entry.count} PRs`}>
                  <i style={{ width: `${Math.max((entry.medianHours / maxCycle) * 100, 1).toFixed(1)}%` }} />
                  <span style={{ position: 'absolute', top: -3, left: `${((entry.p75Hours / maxCycle) * 100).toFixed(1)}%`, width: 2, height: 14, background: 'var(--muted)' }} />
                </div>
              </div>
            ))}
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Very short times usually mean the author merged without waiting for a review.</span>
          </section>
        </aside>
      </div>
    </>
  );
}
